/**
 * First-visit welcome popup — luxury language + currency selector.
 *
 * Behaviour:
 *   1. On mount, if BOTH language AND currency preferences are unset
 *      (first-time visitor), call `GET /api/geo/detect` to retrieve a
 *      suggested locale + currency based on the visitor's IP.
 *   2. Apply the suggestion silently (without marking `prefSet=true`) so
 *      the rest of the UI renders in the right language while the popup
 *      is open.
 *   3. Render a centered luxury modal with two dropdowns + Confirm.
 *      Confirm → marks both prefs as "set" → popup never reappears.
 *   4. Closing the modal (X / backdrop click) also marks prefs as set —
 *      we don't pester the visitor again.
 *
 * Priority order on returning visits:
 *   saved cookie/localStorage  >  URL locale  >  browser language  >  geo IP
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLang, BACKEND_LANG_TO_CODE } from "../context/LanguageContext";
import { useCurrency } from "../context/CurrencyContext";
import api from "../lib/api";
import { CODE_TO_URL, stripLocale, prefixLocale } from "../lib/i18nRouter";

const WelcomeLocalePopup = () => {
  const { lang, change: changeLang, markPrefSet: markLangSet, prefSet: langPrefSet, supported: langSupported } = useLang();
  const { currency, change: changeCurrency, markPrefSet: markCurSet, prefSet: curPrefSet, supported: curSupported } = useCurrency();
  const navigate = useNavigate();
  const location = useLocation();

  // The popup is shown only when BOTH prefs are unset. Either one being set
  // (e.g. user already picked a currency in a prior visit) suppresses it.
  const firstVisit = !langPrefSet && !curPrefSet;
  const [open, setOpen] = useState(firstVisit);
  const detectedRef = useRef(false);

  // Local form state — initialised to current context values.
  const [pickLang, setPickLang] = useState(lang);
  const [pickCur, setPickCur] = useState(currency);

  useEffect(() => { setPickLang(lang); }, [lang]);
  useEffect(() => { setPickCur(currency); }, [currency]);

  // Geo-IP detect (one-shot, only on first visit)
  useEffect(() => {
    if (!firstVisit || detectedRef.current) return;
    detectedRef.current = true;
    api.get("/geo/detect").then(({ data }) => {
      const suggestedCode = BACKEND_LANG_TO_CODE[data?.language] || null;
      if (suggestedCode && suggestedCode !== lang) {
        changeLang(suggestedCode, { fromUser: false });
        setPickLang(suggestedCode);
      }
      const suggestedCur = (data?.currency || "").toUpperCase();
      if (suggestedCur && curSupported.includes(suggestedCur) && suggestedCur !== currency) {
        changeCurrency(suggestedCur, { fromUser: false });
        setPickCur(suggestedCur);
      }
    }).catch(() => { /* no-op — keep browser defaults */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstVisit]);

  // Translated copy — falls back to English when a key isn't translated yet.
  const COPY = useMemo(() => ({
    EN: { title: "Welcome to Opik Jewels", body: "Select your language and currency for the best experience.", language: "Language", currency: "Currency", confirm: "Confirm" },
    ID: { title: "Selamat datang di Opik Jewels", body: "Pilih bahasa dan mata uang Anda untuk pengalaman terbaik.", language: "Bahasa", currency: "Mata Uang", confirm: "Konfirmasi" },
    CN: { title: "欢迎光临 Opik Jewels", body: "请选择您的语言与货币,以获得最佳体验。", language: "语言", currency: "货币", confirm: "确认" },
    TH: { title: "ยินดีต้อนรับสู่ Opik Jewels", body: "เลือกภาษาและสกุลเงินของคุณเพื่อประสบการณ์ที่ดีที่สุด", language: "ภาษา", currency: "สกุลเงิน", confirm: "ยืนยัน" },
    AR: { title: "مرحباً بك في Opik Jewels", body: "اختر لغتك وعملتك للحصول على أفضل تجربة.", language: "اللغة", currency: "العملة", confirm: "تأكيد" },
  }[lang] || {}), [lang]);

  const close = (confirmed) => {
    setOpen(false);
    markLangSet();
    markCurSet();
    if (confirmed) {
      changeLang(pickLang);
      changeCurrency(pickCur);
      // If the URL locale doesn't match the chosen language, swap it.
      const desiredSlug = CODE_TO_URL[pickLang];
      const { locale, rest } = stripLocale(location.pathname);
      if (desiredSlug && desiredSlug !== locale) {
        navigate(prefixLocale(desiredSlug, rest) + location.search + location.hash, { replace: true });
      }
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="welcome-locale-popup"
      className="fixed inset-0 z-[120] flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-title"
    >
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={() => close(false)}
        aria-hidden="true"
      />
      <div className="relative w-full max-w-md bg-noir border border-gold/30 shadow-2xl">
        {/* Gold hairline accent at top */}
        <div className="h-px bg-gradient-to-r from-transparent via-gold to-transparent" />
        <div className="px-8 pt-10 pb-6 text-center">
          <div className="font-accent text-[10px] tracking-[0.32em] uppercase text-gold mb-3">Opik Jewels</div>
          <h2 id="welcome-title" className="font-heading text-3xl text-ivory font-light tracking-wide leading-snug">
            {COPY.title}
          </h2>
          <p className="text-white/55 text-sm mt-4 font-light leading-relaxed max-w-sm mx-auto">
            {COPY.body}
          </p>
        </div>

        <div className="px-8 pb-6 space-y-5">
          <div>
            <label className="block font-accent text-[10px] tracking-[0.28em] uppercase text-white/40 mb-2">{COPY.language}</label>
            <select
              data-testid="welcome-language"
              value={pickLang}
              onChange={(e) => setPickLang(e.target.value)}
              className="w-full bg-transparent border border-white/20 hover:border-gold/60 focus:border-gold focus:outline-none px-4 py-3 text-white text-sm tracking-wide transition-colors"
            >
              {langSupported.map((l) => (
                <option key={l.value} value={l.value} className="bg-noir">
                  {l.flag}  {l.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block font-accent text-[10px] tracking-[0.28em] uppercase text-white/40 mb-2">{COPY.currency}</label>
            <select
              data-testid="welcome-currency"
              value={pickCur}
              onChange={(e) => setPickCur(e.target.value)}
              className="w-full bg-transparent border border-white/20 hover:border-gold/60 focus:border-gold focus:outline-none px-4 py-3 text-white text-sm tracking-wide transition-colors"
            >
              {curSupported.map((c) => (
                <option key={c} value={c} className="bg-noir">{c}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="px-8 pb-10">
          <button
            data-testid="welcome-confirm"
            onClick={() => close(true)}
            className="w-full bg-gold text-noir font-accent text-[11px] tracking-[0.32em] uppercase py-4 hover:bg-gold/90 transition-colors"
          >
            {COPY.confirm}
          </button>
          <p className="text-white/30 text-[10px] tracking-[0.18em] uppercase font-accent text-center mt-4">
            You can change this anytime in the header
          </p>
        </div>

        {/* Gold hairline accent at bottom */}
        <div className="h-px bg-gradient-to-r from-transparent via-gold to-transparent" />
      </div>
    </div>
  );
};

export default WelcomeLocalePopup;
